import PalletizerHub from './context/PalletizerContext';
import Calibration from './parts/calibration/Calibration';
import CollectionsPage from './parts/CollectionsPage';
import Configuration from './parts/Configuration';
import { useMachineConfig } from './parts/General';
import PalletizerPage from './parts/operation/OperationPage';
import { LayoutContent } from './parts/Layout';
import Settings, { ConfirmationModal } from './parts/Settings';
import { UserSelectionModal } from './parts/Preferences';
import Sidebar, { TabOption } from './parts/Sidebar';
import { AppFactory, isWebDeployment } from './parts/shared/shared';
import './scss/App.scss';
import './scss/Fonts.scss';
import {
	Application,
	PalletConfig,
	PalletizerStatus,
	UnitsSystem,
	Language,
	UserProfiles,
} from 'libapi';
import React, {
	createContext,
	useState,
	useEffect,
	useMemo,
	ReactElement,
} from 'react';
import {
	BrowserRouter as Router,
	Switch,
	Route,
	useLocation,
	Redirect,
} from 'react-router-dom';
import { AssetsProvider } from './context/AssetsContext';

export const ApplicationContext = createContext<Partial<Application>>({});
export const PreferencesContext = createContext({});
export const ConfigurationsContext = createContext({});

export enum ConfigStates {
	NoConfigs,
	Editing,
}

export enum Page {
	Palletizer,
	Collections,
	Configuration,
	ConfigurationPage, // Todo when retiring old config rename to just configuration
	Preferences,
	Settings,
	Calibration,
	// Settings,
}

export interface AppTabOption extends TabOption {
	component(pTab: AppTabOption): ReactElement;
}

const IS_WEB_DEPLOYMENT = isWebDeployment();

const gTabOptions: AppTabOption[] = [];

gTabOptions[Page.Palletizer] = {
	Name: 'Palletizer',
	IconClass: 'icon-home_inactive',
	disabled: false,
	component: function (pTab) {
		return <PalletizerPage pTab={pTab} />;
	},
};
gTabOptions[Page.Configuration] = {
	Name: 'Configuration',
	IconClass: 'icon-design',
	disabled: false,
	component: function (pTab) {
		return <Configuration pTab={pTab} />;
	},
};
gTabOptions[Page.Collections] = {
	Name: 'Collections',
	IconClass: 'icon-part',
	disabled: IS_WEB_DEPLOYMENT,
	component: function (pTab) {
		return <CollectionsPage pTab={pTab} />;
	},
};
gTabOptions[Page.Settings] = {
	Name: 'Settings',
	IconClass: 'icon-cog',
	disabled: false,
	component: function (pTab) {
		return <Settings pTab={pTab} />;
	},
};
gTabOptions[Page.Calibration] = {
	Name: 'Calibration',
	IconClass: 'icon-scope-of-work',
	disabled: false,
	component: function (pTab) {
		return <Calibration pTab={pTab} />;
	},
};

export default function App() {
	const lApp = useMemo(function () {
		return AppFactory();
	}, []);

	const [lCurrentPage, lSetCurrentPage] = useState<Page>(Page.Palletizer);
	const lLocation = useLocation();
	const [preferences, setPreferences] = useState({
		units_system: UnitsSystem.METRIC,
		robot_configurations: '',
		language: Language.ENGLISH,
		active_user_name: '',
	});

	const [lAvailableTabs, lSetAvailableTabs] = useState<AppTabOption[]>([]);
	const [lEnabledTabs, lSetEnabledTabs] = useState<AppTabOption[]>([]);

	const [showConfirmReconnect, setShowConfirmReconnect] =
		useState<boolean>(false);
	const [showUserSelect, setShowUserSelect] = useState<boolean>(false);
	const [showVentionUser, setShowVentionUser] = useState<boolean>(false);
	const [showControlCenter, setShowControlCenter] = useState<boolean>(false);
	const [selectedUserObject, setSelectedUserObject] =
		useState<UserProfiles | null>(null);

	useEffect(
		function () {
			for (let i = 0; i < lAvailableTabs.length; i++) {
				const lName = lAvailableTabs[i].Name;
				const lReg = new RegExp(lName, 'i');
				if (!lReg.test(lLocation.pathname.replace('/', '').replaceAll(/\s+/g, '')))
					continue;
				lSetCurrentPage(i);

				return;
			}
		},
		[lLocation, lAvailableTabs]
	);

	useEffect(() => {
		(async () => {
			try {
				const pConfig = await lApp.api.Machine.get();
				if (!pConfig) {
					throw new Error('No machine config');
				}

				const lDefaultPreferences = {
					units_system: UnitsSystem.IMPERIAL,
					cached_gap_distance: 10,
					robot_configurations:
						'{"distance" : {"approach" : {"x" : 50, "y" : 50, "z" : 50 }, "pick" : 250}, "speed" : {"cruise" : 1000, "approach" : 500}, "acc" : {"cruise" : 2500, "approach" : 1000}}',
					language: Language.ENGLISH,
					users: [{ "name": "Administrator", "access_level": 95, "disabled_pages": [] }, { "name": "Supervisor", "access_level": 20, "disabled_pages": [ "Settings" ] }, { "name": "Operator", "access_level": 10, "disabled_pages": [ "Settings", "Calibration", "ControlCenter", "Configuration" ] }, { "name": "Vention", "access_level": 100, "disabled_pages": [] }],
					active_user_name: "Administrator"
				};
				let lLocalPreferences = JSON.parse(
					localStorage.getItem('vention_palletizer_preferences') ||
						JSON.stringify(lDefaultPreferences)
				);
				if (lLocalPreferences.active_user_name === undefined && isWebDeployment()) {
					lLocalPreferences = lDefaultPreferences;
					lUpdatePreferences(lLocalPreferences);
				} 
				setMappedPreferences(lLocalPreferences);
		

				const genericConfig = await lApp.api.Generic.get();
				const lSelectedUserObject = !IS_WEB_DEPLOYMENT? genericConfig.preferences.users.find(
					(user: UserProfiles) =>
						user.name === genericConfig.preferences.active_user_name
				) : lLocalPreferences.users.find(
					(user: UserProfiles) =>
						user.name === lLocalPreferences.active_user_name
				)
				
				setSelectedUserObject(lSelectedUserObject);

				const lTempTabs = [];

				if (!pConfig.IsMaster) {
					lTempTabs.push(gTabOptions[Page.Palletizer]);
				}

				if (pConfig.IsMaster) {
					lTempTabs.push(gTabOptions[Page.Collections]);
				}
				lTempTabs.push(gTabOptions[Page.Configuration]);
				lTempTabs.push(gTabOptions[Page.Settings]);

				if (!pConfig.IsMaster) {
					lTempTabs.push(gTabOptions[Page.Calibration]);
				} else {
					lSetCurrentPage(Page.Collections);
				}

				// Overwrite disabled tabs based on selectedUserObject.disabled_pages
				const userTabs = lTempTabs.reduce((acc: AppTabOption[], tabOption) => {
					acc.push({
						...tabOption,
						disabled: lSelectedUserObject?.disabled_pages.includes(tabOption.Name),
					});
					return acc;
				}, []);

				lSetAvailableTabs(userTabs);
				lSetEnabledTabs(userTabs.filter(pTab => !pTab.disabled));

				setShowControlCenter(
					!lSelectedUserObject?.disabled_pages.includes('ControlCenter')
				);

				if (IS_WEB_DEPLOYMENT) {
					console.log('SET CURRENT PAGE CONFIG');
					lSetCurrentPage(Page.Palletizer);
				}
			} catch (error) {
				console.error(error);
			}
		})();
	}, [lApp]);

	const [all_configs, _set_all_configs] = useState<PalletConfig>();
	const [config_state, _set_config_state] = useState<ConfigStates>(
		ConfigStates.NoConfigs
	);
	const set_config_state = (cs: ConfigStates) => {
		/*alert("set_config_state");*/ _set_config_state(cs);
	};
	const set_all_configs = (pc: PalletConfig) => {
		/*alert("set_all_config");*/ _set_all_configs(pc);
	};

	const setMappedPreferences = (data: any) => {
		const map = new Map<string, UserProfiles>();
		data.users.forEach((user: UserProfiles) => {
			map.set(user.name, user);
		});
		setPreferences({
			...data,
			users: map,
		});
	};

	const convertMapToArray = (map: Map<string, UserProfiles>): UserProfiles[] => {
		return Array.from(map.values());
	};

	const lUpdatePreferences = function (pPrefs: any) {
		setPreferences(pPrefs);

		const convertedPreferences = {
			...pPrefs,
			users: convertMapToArray(pPrefs.users),
		};

		if (!IS_WEB_DEPLOYMENT) {
			lApp.api.Generic.set(['preferences'], convertedPreferences)
				.then(() => {
					// Additional condition to save the language preference in localStorage
					if (
						convertedPreferences.language !== preferences.language ||
						convertedPreferences.active_user_name !== preferences.active_user_name
					) {
						localStorage.setItem('language', convertedPreferences.language);
						// Refresh the window after setting the language preference in localStorage
						window.location.reload();
					}
				})
				.catch(console.log);
		} else {
			localStorage.setItem(
				'vention_palletizer_preferences',
				JSON.stringify(convertedPreferences)
			);
			if (
				convertedPreferences.language !== preferences.language ||
				convertedPreferences.active_user_name !== preferences.active_user_name
			) {
				localStorage.setItem('language', convertedPreferences.language);
				// Refresh the window after setting the language preference in localStorage
				window.location.reload();
			}
		}
	};

	useEffect(() => {
		const lDefaultPreferences = {
			units_system: UnitsSystem.IMPERIAL,
			cached_gap_distance: 10,
			robot_configurations:
				'{"distance" : {"approach" : {"x" : 50, "y" : 50, "z" : 50 }, "pick" : 250}, "speed" : {"cruise" : 1000, "approach" : 500}, "acc" : {"cruise" : 2500, "approach" : 1000}}',
			language: Language.ENGLISH,
			users: [{ "name": "Administrator", "access_level": 95, "disabled_pages": [] }, { "name": "Supervisor", "access_level": 20, "disabled_pages": [ "Settings" ] }, { "name": "Operator", "access_level": 10, "disabled_pages": [ "Settings", "Calibration", "ControlCenter", "Configuration" ] }],
			active_user_name: "Administrator"
		};

		if (!IS_WEB_DEPLOYMENT) {
			lApp.api.Generic.get('preferences')
				.then(function (data) {
					setMappedPreferences(data);
				})
				.catch(function (e) {
					console.error(e);
				});
			lApp.setPalletizerMode(false);
		} else {
			let lLocalPreferences = JSON.parse(
				localStorage.getItem('vention_palletizer_preferences') ||
					JSON.stringify(lDefaultPreferences)
			);
			if (lLocalPreferences.active_user_name === undefined) {
				lLocalPreferences = lDefaultPreferences;
			} 
			setMappedPreferences(lLocalPreferences);
			// no need for update...
		}
	}, []);

	useEffect(() => {
		if (localStorage.getItem('reloaded') === 'true') {
			alert('A new configuration has been deployed to this system.');
			localStorage.removeItem('reloaded');
		}
	}, []);

	const reload_cfs = () => {
		const psl = JSON.parse(
			localStorage.getItem('vention_palletizer_configuration') || '{}'
		);
		if (
			psl instanceof Array &&
			psl[0].hasOwnProperty('web_version') &&
			psl[0].web_version === 1 && // Verify the decorated version in localStorage (as a simple means of managing backward compatibility).
			psl[0].hasOwnProperty('config') &&
			psl[0].config.hasOwnProperty('boxes') &&
			psl[0].config.hasOwnProperty('pallets') &&
			psl[0].config.hasOwnProperty('slipsheets') &&
			(0 == psl[0].config.pallets.length ||
				0 == psl[0].config.pallets[0].Stack.length ||
				psl[0].config.pallets[0].Stack[0].hasOwnProperty('layers'))
		) {
			set_all_configs(psl[0]);
			set_config_state(ConfigStates.Editing);
			return;
		}

		if (IS_WEB_DEPLOYMENT) {
			localStorage.removeItem('queue');
		}

		lApp.api.Pallet.get()
			.then((ps: PalletConfig) => {
				if (undefined == ps.config.boxes) {
					ps.config.boxes = [];
				}
				if (undefined == ps.config.pallets) {
					ps.config.pallets = [];
				}
				if (undefined == ps.config.slipsheets) {
					ps.config.slipsheets = [];
				}
				set_all_configs(ps);
				// default to editing.
				set_config_state(ConfigStates.Editing);
			})
			.catch(console.log);
	};

	useEffect(() => {
		reload_cfs();
	}, []);

	return (
		<ApplicationContext.Provider value={lApp}>
			<PreferencesContext.Provider
				value={{ preferences, setPreferences: lUpdatePreferences }}
			>
				<ConfigurationsContext.Provider
					value={{
						all_configs,
						config_state,
						set_all_configs,
						set_config_state,
						reload_cfs,
					}}
				>
					<PalletizerHub>
						<AssetsProvider>
							{selectedUserObject && (
								<main
									className="vh-100 vw-100 flex-col blue-gradient"
									style={{
										background:
											selectedUserObject.name === 'Vention' ? 'red' : 'dark-blue',
									}}
								>
									<Sidebar
										showControlCenter={showControlCenter}
										options={lAvailableTabs}
										selected={lCurrentPage}
										setShowConfirmReconnect={setShowConfirmReconnect}
										setShowUserSelect={setShowUserSelect}
										setShowVentionUser={setShowVentionUser}
										selectedUserName={selectedUserObject.name}
									/>
									<Switch>
										{lEnabledTabs.map(function (pTab, pTabIndex) {
											return (
												<Route
													key={pTabIndex}
													path={'/' + pTab.Name.replaceAll(/\s+/g, '')}
												>
													{pTab.component(pTab)}
												</Route>
											);
										})}
										<Route path="/">
											{(function () {
												if (lEnabledTabs.length === 0)
													return (
														<span className="background-white full-size flex-center align-center color-blue font-6">
															Loading
														</span>
													);
												return (
													<Redirect to={'/' + lEnabledTabs[0].Name.replaceAll(/\s+/g, '')} />
												);
											})()}
										</Route>
									</Switch>
									{showConfirmReconnect && (
										<ConfirmationModal
											t1={`This will reconnect to the robot.`}
											t2={'Are you sure?'}
											bCancel={'Cancel'}
											bConfirm={'Reconnect'}
											onConfirm={() => {
												setShowConfirmReconnect(false);
												lApp.restartCore();
											}}
											onCancel={() => {
												setShowConfirmReconnect(false);
											}}
											onOutsideClick={() => {
												setShowConfirmReconnect(false);
											}}
										/>
									)}
									{showUserSelect && (
										<LayoutContent
											pTab={{ Name: ' ', IconClass: ' ' }}
											hideHeader={true}
											modal={true}
										>
											<UserSelectionModal
												t1={`User Selection`}
												bCancel={'Cancel'}
												bConfirm={'Confirm'}
												setShowUserSelect={setShowUserSelect}
												showVentionUser={showVentionUser}
											/>
										</LayoutContent>
									)}
								</main>
							)}
						</AssetsProvider>
					</PalletizerHub>
				</ConfigurationsContext.Provider>
			</PreferencesContext.Provider>
		</ApplicationContext.Provider>
	);
}
